/**
 * "memory": pairs of cards (memory1Elements[i] ↔ memory2Elements[i]), shuffled
 * (PlayspaceMemory.cs / MemoryElementPlayer.cs). Two open cards that belong
 * to the same pair stay open; otherwise they flip back after 2 s. Items =
 * number of pairs. A wrong pair counts as an error but never ends the slide.
 */
import { useMemo, useRef, useState } from "react";
import type { Element as El, SlideOf } from "@makeit/schema";
import { ElementImage, TextBox, useElementSound } from "../elements";
import { useActivity } from "../activity";
import { useFx } from "../fx";
import { shuffled } from "../util";
import { libraryUrl } from "../../data/media";

interface Card {
  key: string;
  pair: number;
  element: El;
}

export function MemorySlide({ slide }: { slide: SlideOf<"memory"> }) {
  const pairs = Math.min(slide.memory1Elements.length, slide.memory2Elements.length);
  const activity = useActivity(Math.max(1, pairs));
  const cards = useMemo<Card[]>(() => {
    const list: Card[] = [];
    for (let i = 0; i < pairs; i++) {
      list.push({ key: `a${i}`, pair: i, element: slide.memory1Elements[i] });
      list.push({ key: `b${i}`, pair: i, element: slide.memory2Elements[i] });
    }
    return shuffled(list);
  }, [slide, pairs]);

  const [open, setOpen] = useState<string[]>([]);
  const [matched, setMatched] = useState<Set<string>>(new Set());
  const busy = useRef(false);
  const fx = useFx();
  const rootRef = useRef<HTMLDivElement>(null);

  const flip = (card: Card) => {
    if (busy.current || open.includes(card.key) || matched.has(card.key) || activity.isDone()) return;
    const next = [...open, card.key];
    setOpen(next);
    if (next.length === 2) {
      busy.current = true;
      const [a, b] = next.map((k) => cards.find((c) => c.key === k)!);
      if (a.pair === b.pair) {
        window.setTimeout(() => {
          setMatched((m) => new Set([...m, a.key, b.key]));
          setOpen([]);
          busy.current = false;
          rootRef.current?.querySelectorAll<HTMLElement>(`[data-key="${a.key}"], [data-key="${b.key}"]`).forEach((el) => fx.stars(el));
          activity.correct();
        }, 700);
      } else {
        activity.wrong(true);
        window.setTimeout(() => {
          setOpen([]);
          busy.current = false;
        }, 2000);
      }
    }
  };

  // Grid from PlayspaceMemory.ResizeGrid: columns and cell size by pair count.
  const cols = pairs <= 2 ? 2 : pairs <= 8 ? 4 : 5;
  const divisor = pairs <= 2 ? 3 : pairs <= 6 ? 3.5 : pairs <= 8 ? 4.5 : 5;
  const cell = Math.floor(1300 / divisor);

  return (
    <div className="memory" ref={rootRef}>
      <div className="memory-grid" style={{ gridTemplateColumns: `repeat(${cols}, ${cell}px)`, gridAutoRows: `${cell}px` }}>
        {cards.map((c) => (
          <MemoryCard key={c.key} card={c} faceUp={open.includes(c.key) || matched.has(c.key)} matched={matched.has(c.key)} onFlip={() => flip(c)} />
        ))}
      </div>
    </div>
  );
}

function MemoryCard({ card, faceUp, matched, onFlip }: { card: Card; faceUp: boolean; matched: boolean; onFlip: () => void }) {
  const { play, hasSound } = useElementSound(card.element);
  const wasUp = useRef(false);
  if (faceUp && !wasUp.current && hasSound) play();
  wasUp.current = faceUp;
  return (
    <div className={`memory-card ${faceUp ? "up" : ""} ${matched ? "matched" : ""}`} data-key={card.key} data-pair={card.pair} onClick={onFlip}>
      <div className="memory-inner">
        <div className="memory-back" style={{ backgroundImage: `url(${libraryUrl("backgrounds/memory.png")})` }} />
        <div className="memory-front">
          {card.element.image && <ElementImage element={card.element} />}
          <TextBox text={card.element.text} style={{ position: "absolute", inset: 0 }} fit />
        </div>
      </div>
    </div>
  );
}
